import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { useTheme } from "../../../contexts/ThemeContext";
import { useApi } from "../../../hooks/useApi";
import { 
  MapPin, 
  Navigation, 
  Gauge, 
  Clock, 
  Activity, 
  RefreshCw,
  Play,
  Pause,
  Square,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Search,
  X,
  Smartphone,
  Satellite,
  Calendar,
  Hash,
  ArrowLeft
} from "lucide-react";
import { Input } from "../../../components/ui/input";

export default function LiveMap() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const { get, loading: apiLoading } = useApi();
  
  // Determine the correct back path based on current route
  const getBackPath = () => {
    if (location.pathname.startsWith('/customer/')) {
      return '/customer/dashboard';
    }
    return '/admin/map/trips';
  };
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const vehicleMarkerRef = useRef(null);
  const allVehicleMarkersRef = useRef([]); // Store all vehicle markers
  const deviceFullDataMapRef = useRef(new Map()); // Store full location data for each device
  const routeIndexRef = useRef(0);
  const progressRef = useRef(0); // Progress between 0 and 1 for interpolationå
  const intervalRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isPlayingRef = useRef(true); // Use ref to avoid stale closure
  const currentBearingRef = useRef(0); // Current rotation angle in degrees
  const routePolylineRef = useRef(null);
  const startPointMarkerRef = useRef(null); // Store start point marker with "S" label
  const previousPositionsRef = useRef(new Map()); // Store previous positions for smooth animation
  const markerAnimationsRef = useRef(new Map()); // Store active animations for each marker
  const refreshIntervalRef = useRef(null); // Store interval for auto-refresh
  const devicePolylinesRef = useRef(new Map()); // Store polylines for each device's path
  const devicePathsRef = useRef(new Map()); // Store path points for each device [{lat, lng}, ...]
  const isInitialLoadRef = useRef(true); // Track if this is the initial load
  const shouldCenterOnRouteRef = useRef(false); // Track if we should center on route change (only on selection change, not data updates)
  const shouldFitBoundsRef = useRef(false); // Track if we should fit bounds (only on initial load or selection clear)
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMobileCardOpen, setIsMobileCardOpen] = useState(false); // Current location card collapse state (false = closed by default on mobile)
  const [isDeviceCardOpen, setIsDeviceCardOpen] = useState(false); // Device selector card collapse state (false = closed by default on mobile)
  const [deviceSearchQuery, setDeviceSearchQuery] = useState(""); // Search query for devices
  const [isMobile, setIsMobile] = useState(false); // Track if device is mobile
  const [cardState, setCardState] = useState('normal'); // Track card animation state: 'normal', 'expanded', 'collapsed'
  const [isDragging, setIsDragging] = useState(false); // Track if card is being dragged
  const [dragStartY, setDragStartY] = useState(0); // Track drag start position
  const [cardHeight, setCardHeight] = useState(40); // Track card height percentage
  const [isMobileVehicleCardOpen, setIsMobileVehicleCardOpen] = useState(false); // Control mobile vehicle card visibility
  const [isMobileDeviceDropdownOpen, setIsMobileDeviceDropdownOpen] = useState(false); // Control mobile device dropdown open state
  const [isDesktopDeviceDropdownOpen, setIsDesktopDeviceDropdownOpen] = useState(false); // Control desktop device dropdown open state
  const [deviceVehicleMap, setDeviceVehicleMap] = useState(new Map()); // Map device_id to vehicle info
  const [locationsData, setLocationsData] = useState([]); // All location data from API
  const [selectedMarkerData, setSelectedMarkerData] = useState(null); // Selected marker's full data for detailed card
  const [devices, setDevices] = useState([]); // Unique devices from API with vehicle info
  const [selectedDeviceId, setSelectedDeviceId] = useState(null); // Currently tracked device
  const [route, setRoute] = useState([]); // Route points for selected device
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [error, setError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState({
    lat: 17.385044,
    lng: 78.486671,
    speed: 35,
    status: "Moving",
    timestamp: new Date().toLocaleTimeString(),
    address: "Hyderabad, Telangana, India"
  });

  // Get start point marker icon (green circle with "S") - same as SingleVehicleMap
  const getStartPointIcon = () => {
    const size = 36;
    const radius = 16;
    const StartIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size/2}" cy="${size/2}" r="${radius + 2}" fill="#10b981" opacity="0.3"/>
      <circle cx="${size/2}" cy="${size/2}" r="${radius}" fill="#10b981" stroke="#ffffff" stroke-width="3" opacity="0.95"/>
      <circle cx="${size/2}" cy="${size/2}" r="${radius - 3}" fill="#059669" opacity="0.9"/>
      <text x="${size/2}" y="${size/2 + 6}" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="#ffffff" text-anchor="middle">S</text>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(StartIcon)}`;
  };

  // Get RED car icon SVG (vertical, rotation-friendly)
  const getCarIconRed = () => {
    const CarIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="64" viewBox="0 0 300 600">
      <!-- Shadow base -->
      <ellipse cx="150" cy="300" rx="95" ry="240" fill="#000" opacity="0.15"/>
      
      <!-- Car body -->
      <path d="M90 60 Q150 20 210 60 L235 150 Q245 300 235 450 L210 540 Q150 580 90 540 L65 450 Q55 300 65 150 Z" fill="#dc2626" stroke="#991b1b" stroke-width="2"/>
      
      <!-- Car body highlight -->
      <path d="M90 60 Q150 20 210 60 L235 150 Q245 300 235 450 L210 540 Q150 580 90 540 L65 450 Q55 300 65 150 Z" fill="#ef4444" opacity="0.3"/>
      
      <!-- Windshield -->
      <rect x="105" y="110" width="90" height="90" rx="18" fill="#bae6fd" opacity="0.7"/>
      
      <!-- Rear glass -->
      <rect x="105" y="380" width="90" height="90" rx="18" fill="#bae6fd" opacity="0.7"/>
      
      <!-- Hood detail -->
      <rect x="140" y="80" width="20" height="120" rx="10" fill="#ffffff" opacity="0.25"/>
      
      <!-- Wheels -->
      <rect x="45" y="170" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="45" y="350" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="225" y="170" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="225" y="350" width="30" height="80" rx="14" fill="#111827"/>
      
      <!-- Wheel rims -->
      <rect x="52" y="188" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="52" y="368" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="232" y="188" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="232" y="368" width="16" height="44" rx="8" fill="#9ca3af"/>
      
      <!-- Headlights -->
      <rect x="105" y="60" width="40" height="12" rx="6" fill="#fde047"/>
      <rect x="155" y="60" width="40" height="12" rx="6" fill="#fde047"/>
      
      <!-- Tail lights -->
      <rect x="105" y="528" width="40" height="12" rx="6" fill="#7f1d1d"/>
      <rect x="155" y="528" width="40" height="12" rx="6" fill="#7f1d1d"/>
      
      <!-- Center direction line -->
      <line x1="150" y1="50" x2="150" y2="550" stroke="#ffffff" stroke-width="3" stroke-dasharray="10,10" opacity="0.5"/>
    </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(CarIcon)}`;
  };

  // Linear interpolation function for smooth movement
  const lerp = (start, end, t) => {
    return start + (end - start) * t;
  };

  // Easing function for smoother animation (ease-in-out)
  const easeInOutCubic = (t) => {
    return t < 0.5 
      ? 4 * t * t * t 
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Calculate distance between two points
  const getDistance = (point1, point2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Calculate bearing (angle) between two points in degrees
  const getBearing = (point1, point2) => {
    const lat1 = point1.lat * Math.PI / 180;
    const lat2 = point2.lat * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;

    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    const bearing = Math.atan2(y, x);
    return (bearing * 180 / Math.PI + 360) % 360; // Convert to degrees and normalize to 0-360
  };

  // Check if marker has setRotation method
  const hasSetRotation = (marker) => {
    return marker && typeof marker.setRotation === 'function';
  };

  // Smoothly animate marker from old position to new position
  const animateMarkerToNewPosition = (marker, oldPos, newPos, duration = 2000) => {
    if (!marker || !oldPos || !newPos) return;

    // Cancel any existing animation for this marker
    const existingAnimation = markerAnimationsRef.current.get(marker);
    if (existingAnimation) {
      cancelAnimationFrame(existingAnimation.frameId);
    }

    const startTime = Date.now();
    const startLat = oldPos.lat;
    const startLng = oldPos.lng;
    const endLat = newPos.lat;
    const endLng = newPos.lng;

    // Calculate initial bearing
    const startBearing = getBearing(oldPos, newPos);
    let currentBearing = startBearing;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Use easing function for smooth animation
      const easedProgress = easeInOutCubic(progress);

      // Interpolate position
      const currentLat = lerp(startLat, endLat, easedProgress);
      const currentLng = lerp(startLng, endLng, easedProgress);

      // Update marker position
      try {
        if (marker && typeof marker.setPosition === 'function') {
          marker.setPosition({ lat: currentLat, lng: currentLng });
          
          // Smoothly pan map camera to follow marker (only for selected vehicle)
          if (marker === vehicleMarkerRef.current && mapInstance.current) {
            const now = Date.now();
            if (!mapInstance.current.lastPan || now - mapInstance.current.lastPan > 100) {
              mapInstance.current.lastPan = now;
              // Mappls uses [longitude, latitude] format
              mapInstance.current.panTo(
                [currentLng, currentLat],
                { duration: 150 }
              );
            }
          }
        }
      } catch (error) {
      }

      // Update rotation smoothly
      const targetBearing = getBearing(
        { lat: currentLat, lng: currentLng },
        { lat: endLat, lng: endLng }
      );
      
      // Smooth rotation transition
      let angleDiff = targetBearing - currentBearing;
      if (angleDiff > 180) angleDiff -= 360;
      if (angleDiff < -180) angleDiff += 360;
      
      const rotationSpeed = 0.2;
      currentBearing = (currentBearing + angleDiff * rotationSpeed + 360) % 360;

      if (hasSetRotation(marker)) {
        marker.setRotation(currentBearing);
      } else {
        // Fallback: Apply CSS transform rotation
        const element = marker?.getElement ? marker.getElement() : null;
        if (element) {
          const img = element.querySelector('img');
          const targetElement = img || element;
          if (targetElement) {
            targetElement.style.transition = 'transform 0.1s ease-out';
            targetElement.style.transform = `rotate(${currentBearing}deg)`;
          }
        }
      }

      if (progress < 1) {
        const frameId = requestAnimationFrame(animate);
        markerAnimationsRef.current.set(marker, { frameId, startTime });
      } else {
        // Animation complete
        markerAnimationsRef.current.delete(marker);
      }
    };

    const frameId = requestAnimationFrame(animate);
    markerAnimationsRef.current.set(marker, { frameId, startTime });
  };

  // Fit map bounds to show all vehicle locations
  const fitMapToAllVehicles = (map, locations) => {
    if (!map || !locations || locations.length === 0) return;

    try {
      // Get unique device locations (latest location for each device)
      const deviceLocations = new Map();
      locations.forEach(loc => {
        const deviceId = loc.device_id;
        if (!deviceLocations.has(deviceId)) {
          deviceLocations.set(deviceId, {
            lat: loc.latitude,
            lng: loc.longitude,
            device_id: deviceId
          });
        }
      });

      const allPoints = Array.from(deviceLocations.values());
      
      if (allPoints.length === 0) return;

      // If only one vehicle, just center on it
      // Mappls uses [longitude, latitude] format
      if (allPoints.length === 1) {
        map.setCenter([allPoints[0].lng, allPoints[0].lat]);
        map.setZoom(12);
        return;
      }

      // Calculate bounds for multiple vehicles
      const lats = allPoints.map(p => p.lat);
      const lngs = allPoints.map(p => p.lng);
      
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // Add padding
      const latPadding = Math.max((maxLat - minLat) * 0.1, 0.01);
      const lngPadding = Math.max((maxLng - minLng) * 0.1, 0.01);

      // Create bounds [southwest, northeast] format: [[lng, lat], [lng, lat]]
      const bounds = [
        [minLng - lngPadding, minLat - latPadding], // Southwest
        [maxLng + lngPadding, maxLat + latPadding]  // Northeast
      ];

      // Fit bounds with padding
      if (map.fitBounds) {
        map.fitBounds(bounds, {
          padding: 100,
          maxZoom: 14,
          duration: 1000
        });
      } else {
        // Fallback: calculate center and zoom
        // Mappls uses [longitude, latitude] format
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        map.setCenter([centerLng, centerLat]);
        
        // Calculate appropriate zoom level
        const latDiff = maxLat - minLat;
        const lngDiff = maxLng - minLng;
        const maxDiff = Math.max(latDiff, lngDiff);
        let zoom = 15;
        if (maxDiff > 0.1) zoom = 12;
        else if (maxDiff > 0.05) zoom = 13;
        else if (maxDiff > 0.01) zoom = 14;
        map.setZoom(zoom);
      }
    } catch (error) {
    }
  };

  // Update or create polyline path for a device when position changes
  const updateDevicePath = (map, deviceId, previousPos, newPos) => {
    if (!map || !deviceId || !newPos) return;

    // Get or create path array for this device
    let devicePath = devicePathsRef.current.get(deviceId);
    if (!devicePath) {
      devicePath = [];
      devicePathsRef.current.set(deviceId, devicePath);
    }

    // If there's a previous position, add it to the path if it's not already there
    if (previousPos) {
      // Check if previous position is already the last point in path
      const lastPoint = devicePath.length > 0 ? devicePath[devicePath.length - 1] : null;
      if (!lastPoint || 
          Math.abs(lastPoint.lat - previousPos.lat) > 0.000001 || 
          Math.abs(lastPoint.lng - previousPos.lng) > 0.000001) {
        // Add previous position if it's different from last point
        devicePath.push({ lat: previousPos.lat, lng: previousPos.lng });
      }
    }

    // Add new position to path
    const lastPoint = devicePath.length > 0 ? devicePath[devicePath.length - 1] : null;
    if (!lastPoint || 
        Math.abs(lastPoint.lat - newPos.lat) > 0.000001 || 
        Math.abs(lastPoint.lng - newPos.lng) > 0.000001) {
      devicePath.push({ lat: newPos.lat, lng: newPos.lng });
    }

    // Get or create polyline for this device
    let polyline = devicePolylinesRef.current.get(deviceId);
    
    if (polyline) {
      // Update existing polyline path with enhanced styling
      try {
        if (typeof polyline.setPath === 'function') {
          polyline.setPath(devicePath);
          // Update styling to maintain enhanced appearance
          if (typeof polyline.setOptions === 'function') {
            const updateOptions = {
              strokeColor: "#3b82f6",
              strokeOpacity: 0.85,
              strokeWeight: 6,
            };
            // Add icons if supported
            if (devicePath.length > 2 && window.mappls?.SymbolPath) {
              try {
                updateOptions.icons = [{
                  icon: {
                    path: window.mappls.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 3,
                    strokeColor: "#1e40af",
                    strokeWeight: 2,
                    fillColor: "#3b82f6",
                    fillOpacity: 0.8,
                  },
                  offset: '50%',
                  repeat: '100px'
                }];
              } catch (iconError) {
                // Icons optional
              }
            }
            polyline.setOptions(updateOptions);
          }
        } else if (typeof polyline.setOptions === 'function') {
          const updateOptions = { 
            path: devicePath,
            strokeColor: "#3b82f6",
            strokeOpacity: 0.85,
            strokeWeight: 6,
          };
          // Add icons if supported
          if (devicePath.length > 2 && window.mappls?.SymbolPath) {
            try {
              updateOptions.icons = [{
                icon: {
                  path: window.mappls.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 3,
                  strokeColor: "#1e40af",
                  strokeWeight: 2,
                  fillColor: "#3b82f6",
                  fillOpacity: 0.8,
                },
                offset: '50%',
                repeat: '100px'
              }];
            } catch (iconError) {
              // Icons optional
            }
          }
          polyline.setOptions(updateOptions);
        } else {
          // If update methods don't work, remove and recreate
          try {
            if (typeof polyline.remove === 'function') {
              polyline.remove();
            }
            // Also remove shadow if it exists
            const shadowPolyline = devicePolylinesRef.current.get(`${deviceId}_shadow`);
            if (shadowPolyline && typeof shadowPolyline.remove === 'function') {
              shadowPolyline.remove();
              devicePolylinesRef.current.delete(`${deviceId}_shadow`);
            }
          } catch (error) {
          }
          polyline = null;
        }
        
        // Update shadow polyline if it exists
        const shadowPolyline = devicePolylinesRef.current.get(`${deviceId}_shadow`);
        if (shadowPolyline && devicePath.length > 3) {
          try {
            if (typeof shadowPolyline.setPath === 'function') {
              shadowPolyline.setPath(devicePath);
            } else if (typeof shadowPolyline.setOptions === 'function') {
              shadowPolyline.setOptions({ 
                path: devicePath,
                strokeColor: "#1e3a8a",
                strokeOpacity: 0.4,
                strokeWeight: 8,
              });
            }
          } catch (shadowError) {
          }
        }
      } catch (error) {
        // Try to remove and recreate
        try {
          if (typeof polyline.remove === 'function') {
            polyline.remove();
          }
          const shadowPolyline = devicePolylinesRef.current.get(`${deviceId}_shadow`);
          if (shadowPolyline && typeof shadowPolyline.remove === 'function') {
            shadowPolyline.remove();
            devicePolylinesRef.current.delete(`${deviceId}_shadow`);
          }
        } catch (removeError) {
        }
        polyline = null;
      }
    }

    // Create new polyline if it doesn't exist or was removed
    if (!polyline && devicePath.length > 1) {
      try {
        // Create main path polyline with advanced styling
        const polylineOptions = {
          map,
          path: devicePath,
          strokeColor: "#3b82f6", // Vibrant blue color
          strokeOpacity: 0.85,
          strokeWeight: 6, // Thicker line for better visibility
          zIndex: 0, // Lower z-index so markers appear on top
        };
        
        // Add directional arrows if supported (optional enhancement)
        if (devicePath.length > 2 && window.mappls?.SymbolPath) {
          try {
            polylineOptions.icons = [{
              icon: {
                path: window.mappls.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: "#1e40af",
                strokeWeight: 2,
                fillColor: "#3b82f6",
                fillOpacity: 0.8,
              },
              offset: '50%',
              repeat: '100px'
            }];
          } catch (iconError) {
            // Icons are optional, continue without them if not supported
          }
        }
        
        polyline = new window.mappls.Polyline(polylineOptions);
        devicePolylinesRef.current.set(deviceId, polyline);
        
        // Create a shadow/outline polyline for depth effect (if path is long enough)
        if (devicePath.length > 3) {
          try {
            const shadowPolyline = new window.mappls.Polyline({
              map,
              path: devicePath,
              strokeColor: "#1e3a8a", // Darker blue for shadow
              strokeOpacity: 0.4,
              strokeWeight: 8, // Slightly thicker for shadow effect
              zIndex: -1, // Behind main polyline
            });
            // Store shadow polyline reference with device ID
            if (!devicePolylinesRef.current.has(`${deviceId}_shadow`)) {
              devicePolylinesRef.current.set(`${deviceId}_shadow`, shadowPolyline);
            }
          } catch (shadowError) {
            // Shadow polyline is optional, continue if it fails
          }
        }
      } catch (error) {
      }
    }
  };

  // Create or update markers for all vehicles with smooth animation
  const createAllVehicleMarkers = (map, locations) => {
    if (!map || !locations || locations.length === 0) return;

    // Get latest location for each device
    const deviceLocations = new Map();
    locations.forEach(loc => {
      const deviceId = loc.device_id;
      if (!deviceLocations.has(deviceId)) {
        deviceLocations.set(deviceId, {
          lat: loc.latitude,
          lng: loc.longitude,
          device_id: deviceId,
          point_time: loc.point_time
        });
      }
    });

    // Create a map of existing markers by device_id
    const existingMarkersMap = new Map();
    allVehicleMarkersRef.current.forEach(marker => {
      if (marker && marker._deviceId) {
        existingMarkersMap.set(marker._deviceId, marker);
      }
    });

    // Process each device location
    deviceLocations.forEach((location, deviceId) => {
      const isSelected = deviceId === selectedDeviceId;
      const vehicleInfo = deviceVehicleMap.get(deviceId);
      const vehicleNumber = vehicleInfo?.vehicle_number || '';
      const vehicleName = vehicleInfo?.vehicle_name || '';
      const imeiNumber = vehicleInfo?.imei_number || '';
      const displayName = vehicleNumber || vehicleName || imeiNumber || deviceId.substring(0, 8) + '...';

      // Check if marker already exists
      const existingMarker = existingMarkersMap.get(deviceId);
      const previousPosition = previousPositionsRef.current.get(deviceId);
      const newPosition = { lat: location.lat, lng: location.lng };

      // Check if position has changed (with small threshold to avoid unnecessary animations)
      // Using 0.000001 (approximately 0.1 meters) for more sensitive detection
      const positionChanged = !previousPosition || 
        Math.abs(previousPosition.lat - newPosition.lat) > 0.000001 ||
        Math.abs(previousPosition.lng - newPosition.lng) > 0.000001;

      if (existingMarker && positionChanged) {
        // Animate existing marker to new position
        animateMarkerToNewPosition(existingMarker, previousPosition, newPosition, 2000);
        
        // Update path polyline from previous to new position
        updateDevicePath(map, deviceId, previousPosition, newPosition);
        
        // Update stored full location data
        const deviceFullLocations = locations
          .filter(loc => loc.device_id === deviceId)
          .sort((a, b) => new Date(b.point_time) - new Date(a.point_time));
        const fullLocationData = deviceFullLocations.length > 0 ? deviceFullLocations[0] : null;
        if (fullLocationData) {
          existingMarker._fullLocationData = fullLocationData;
        }
      } else if (!existingMarker) {
        // Create new marker if it doesn't exist
        try {
          const carIconUrl = getCarIconRed();
          
          // Get full location data for this device
          const deviceFullLocations = locations
            .filter(loc => loc.device_id === deviceId)
            .sort((a, b) => new Date(b.point_time) - new Date(a.point_time));
          const fullLocationData = deviceFullLocations.length > 0 ? deviceFullLocations[0] : null;
          
          const marker = new window.mappls.Marker({
            map,
            position: { lat: location.lat, lng: location.lng },
            icon: carIconUrl,
            width: 32,
            height: 64,
            anchor: [16, 32],
            popupHtml: isMobile ? `
              <div style="font-size:12px">
                <b>${displayName}</b><br/>
                ${vehicleName ? `${vehicleName}<br/>` : ''}
                ${vehicleInfo?.imei_number ? `IMEI: ${vehicleInfo.imei_number}` : ''}
              </div>
            ` : undefined,
          });

          // Store device ID and full data with marker
          marker._deviceId = deviceId;
          if (fullLocationData) {
            marker._fullLocationData = fullLocationData;
          }

          // Initialize path with current position (path will be built as position updates)
          const initialPath = [{ lat: location.lat, lng: location.lng }];
          devicePathsRef.current.set(deviceId, initialPath);

          // Add click event listener (desktop only)
          if (!isMobile) {
            if (marker && typeof marker.addEventListener === 'function') {
              marker.addEventListener('click', (e) => {
                e?.stopPropagation?.();
                if (marker._fullLocationData) {
                  setSelectedMarkerData(marker._fullLocationData);
                }
              });
            }
            
            setTimeout(() => {
              try {
                const element = marker?.getElement ? marker.getElement() : null;
                if (element) {
                  element.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (marker._fullLocationData) {
                      setSelectedMarkerData(marker._fullLocationData);
                    }
                  });
                  element.style.cursor = 'pointer';
                }
              } catch (error) {
              }
            }, 200);
          }

          // Calculate initial bearing
          let initialBearing = 0;
          const devicePoints = locations
            .filter(loc => loc.device_id === deviceId)
            .sort((a, b) => new Date(a.point_time) - new Date(b.point_time));
          
          if (devicePoints.length > 1) {
            const point1 = { lat: devicePoints[0].latitude, lng: devicePoints[0].longitude };
            const point2 = { lat: devicePoints[1].latitude, lng: devicePoints[1].longitude };
            initialBearing = getBearing(point1, point2);
          }

          if (hasSetRotation(marker)) {
            marker.setRotation(initialBearing);
          } else {
            setTimeout(() => {
              const element = marker?.getElement ? marker.getElement() : null;
              if (element) {
                const img = element.querySelector('img');
                if (img) {
                  img.style.transform = `rotate(${initialBearing}deg)`;
                } else {
                  element.style.transform = `rotate(${initialBearing}deg)`;
                }
              }
            }, 100);
          }

          allVehicleMarkersRef.current.push(marker);
        } catch (error) {
        }
      }

      // Update previous position
      previousPositionsRef.current.set(deviceId, newPosition);
    });

    // Remove markers for devices that no longer exist
    const currentDeviceIds = new Set(deviceLocations.keys());
    allVehicleMarkersRef.current = allVehicleMarkersRef.current.filter(marker => {
      if (marker && marker._deviceId) {
        if (!currentDeviceIds.has(marker._deviceId)) {
          // Remove marker that no longer exists
          try {
            if (typeof marker.remove === 'function') {
              marker.remove();
            }
            // Cancel any animation
            const animation = markerAnimationsRef.current.get(marker);
            if (animation) {
              cancelAnimationFrame(animation.frameId);
              markerAnimationsRef.current.delete(marker);
            }
            previousPositionsRef.current.delete(marker._deviceId);
            
            // Remove polyline for this device
            const polyline = devicePolylinesRef.current.get(marker._deviceId);
            if (polyline) {
              try {
                if (typeof polyline.remove === 'function') {
                  polyline.remove();
                }
              } catch (error) {
              }
              devicePolylinesRef.current.delete(marker._deviceId);
            }
            
            // Remove shadow polyline if it exists
            const shadowPolyline = devicePolylinesRef.current.get(`${marker._deviceId}_shadow`);
            if (shadowPolyline) {
              try {
                if (typeof shadowPolyline.remove === 'function') {
                  shadowPolyline.remove();
                }
              } catch (error) {
              }
              devicePolylinesRef.current.delete(`${marker._deviceId}_shadow`);
            }
            
            // Remove path data
            devicePathsRef.current.delete(marker._deviceId);
          } catch (error) {
          }
          return false;
        }
        return true;
      }
      return true;
    });
  };

  // Extract vehicle info from locations data
  const extractVehicleInfo = (locations) => {
    const vehicleMap = new Map();
    const deviceList = [];
    const fullDataMap = new Map(); // Store full location data for each device
    
    locations.forEach(loc => {
      const deviceId = loc.device_id;
      if (deviceId) {
        // Store full location data (latest for each device)
        if (!fullDataMap.has(deviceId)) {
          fullDataMap.set(deviceId, loc);
        } else {
          // Update if this location is more recent
          const existingLoc = fullDataMap.get(deviceId);
          if (new Date(loc.point_time) > new Date(existingLoc.point_time)) {
            fullDataMap.set(deviceId, loc);
          }
        }
        
        if (!vehicleMap.has(deviceId)) {
          const vehicleInfo = {
            vehicle_number: loc.vehicle_number || '',
            vehicle_name: loc.vehicle_name || '',
            imei_number: loc.imei_number || '',
            sim_number: loc.sim_number || '',
          };
          vehicleMap.set(deviceId, vehicleInfo);
          deviceList.push({
            device_id: deviceId,
            ...vehicleInfo
          });
        }
      }
    });
    
    // Store full data map in a ref for easy access
    deviceFullDataMapRef.current = fullDataMap;
    setDeviceVehicleMap(vehicleMap);
    return deviceList;
  };

  // Fetch locations from API
  const fetchLocations = async (showLoading = true) => {
    // Only show loading on initial load or manual refresh
    if (showLoading) {
      setIsLoadingLocations(true);
    }
    setError(null);
    try {
      const response = await get('/customer/locations');
      
      if (response?.success && Array.isArray(response?.data)) {
        const locations = response.data;
        setLocationsData(locations);
        
        // Extract unique devices with vehicle info
        const deviceList = extractVehicleInfo(locations);
        setDevices(deviceList);
        
        // Update or create markers for all vehicles (with smooth animation)
        if (mapInstance.current && locations.length > 0) {
          createAllVehicleMarkers(mapInstance.current, locations);
          
          // Only fit bounds to show all vehicles on first load or when explicitly requested
          if (!selectedDeviceId && (isInitialLoadRef.current || shouldFitBoundsRef.current)) {
            fitMapToAllVehicles(mapInstance.current, locations);
            isInitialLoadRef.current = false;
            shouldFitBoundsRef.current = false; // Reset flag after use
          }
        }
        
        // Update route only for selected device (if one is selected)
        if (selectedDeviceId) {
          // Update route with latest data
          updateRouteForDevice(selectedDeviceId, locations);
          
          // Update selected vehicle marker position if it exists
          if (vehicleMarkerRef.current) {
            const deviceLocation = locations.find(loc => loc.device_id === selectedDeviceId);
            if (deviceLocation) {
              const newPos = { lat: deviceLocation.latitude, lng: deviceLocation.longitude };
              const prevPos = previousPositionsRef.current.get(selectedDeviceId);
              
              // Check if position has changed (using same threshold as other markers)
              if (prevPos && 
                  (Math.abs(prevPos.lat - newPos.lat) > 0.000001 || 
                   Math.abs(prevPos.lng - newPos.lng) > 0.000001)) {
                // Stop route animation to allow smooth real-time movement
                const wasPlaying = isPlayingRef.current;
                if (wasPlaying && animationFrameRef.current) {
                  cancelAnimationFrame(animationFrameRef.current);
                  animationFrameRef.current = null;
                }
                
                // Animate selected vehicle marker to new real-time position smoothly
                // Use shorter duration (1.5s) for more responsive real-time updates
                animateMarkerToNewPosition(vehicleMarkerRef.current, prevPos, newPos, 1500);
                
                // Update path polyline for selected device (but hide it since route polyline is shown)
                // We still update it in case user clears selection later
                if (mapInstance.current) {
                  updateDevicePath(mapInstance.current, selectedDeviceId, prevPos, newPos);
                  // Hide the path polyline for selected device (blue route polyline is shown instead)
                  const selectedPolyline = devicePolylinesRef.current.get(selectedDeviceId);
                  if (selectedPolyline) {
                    try {
                      // Try to hide the polyline by setting opacity to 0 or removing it from map
                      if (typeof selectedPolyline.setOptions === 'function') {
                        selectedPolyline.setOptions({ strokeOpacity: 0 });
                      } else if (typeof selectedPolyline.setMap === 'function') {
                        selectedPolyline.setMap(null);
                      }
                    } catch (error) {
                    }
                  }
                  // Also hide shadow polyline if it exists
                  const shadowPolyline = devicePolylinesRef.current.get(`${selectedDeviceId}_shadow`);
                  if (shadowPolyline) {
                    try {
                      if (typeof shadowPolyline.setOptions === 'function') {
                        shadowPolyline.setOptions({ strokeOpacity: 0 });
                      } else if (typeof shadowPolyline.setMap === 'function') {
                        shadowPolyline.setMap(null);
                      }
                    } catch (error) {
                    }
                  }
                }
                
                // Update current location state with new position immediately
                setCurrentLocation(prev => ({
                  ...prev,
                  lat: newPos.lat,
                  lng: newPos.lng,
                  timestamp: new Date(deviceLocation.point_time).toLocaleTimeString(),
                  status: "Moving"
                }));
                
                // After animation completes, update route index and resume if needed
                setTimeout(() => {
                  if (route.length > 0) {
                    // Set route index to the latest point (new real-time position)
                    routeIndexRef.current = Math.max(0, route.length - 1);
                    progressRef.current = 0;
                    
                    // Resume route animation if it was playing
                    if (wasPlaying && isPlayingRef.current) {
                      animationFrameRef.current = requestAnimationFrame(animateVehicle);
                    }
                  }
                }, 1600); // Wait for animation to complete (1500ms + 100ms buffer)
              }
              
              // Always update previous position for next comparison
              previousPositionsRef.current.set(selectedDeviceId, newPos);
            }
          }
        } else {
          // Clear route when no device is selected
          setRoute([]);
        }
      } else {
        setError('Invalid response format from API');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch locations');
    } finally {
      if (showLoading) {
        setIsLoadingLocations(false);
      }
    }
  };

  // Update route for a specific device
  const updateRouteForDevice = (deviceId, locations = locationsData) => {
    // Filter locations for this device and sort by point_time (ascending - oldest first)
    const deviceLocations = locations
      .filter(loc => loc.device_id === deviceId)
      .sort((a, b) => new Date(a.point_time) - new Date(b.point_time));
    
    if (deviceLocations.length === 0) {
      setRoute([]);
      return;
    }
    
    // Transform API data to route format
    const routePoints = deviceLocations.map(loc => ({
      lat: loc.latitude,
      lng: loc.longitude,
      address: `Device: ${deviceId.substring(0, 8)}...`, // Placeholder address
      point_time: loc.point_time,
      device_id: loc.device_id
    }));
    
    setRoute(routePoints);
    
    // Reset animation to start from first point
    routeIndexRef.current = 0;
    progressRef.current = 0;
    
    // Update current location to latest point (last in sorted array is most recent)
    if (routePoints.length > 0) {
      const latestPoint = routePoints[routePoints.length - 1];
      setCurrentLocation(prev => ({
        ...prev,
        lat: latestPoint.lat,
        lng: latestPoint.lng,
        timestamp: new Date(latestPoint.point_time).toLocaleTimeString(),
        address: latestPoint.address
      }));
    }
  };

  /* ▶️ Smooth animate vehicle with interpolation */
  const animateVehicle = () => {
    if (!vehicleMarkerRef.current || !mapInstance.current || !isPlayingRef.current || route.length === 0) {
      animationFrameRef.current = null;
      return;
    }

    const currentIndex = routeIndexRef.current;
    const nextIndex = (currentIndex + 1) % route.length;
    const currentPoint = route[currentIndex];
    const nextPoint = route[nextIndex];

    if (!currentPoint || !nextPoint) {
      animationFrameRef.current = null;
      return;
    }

    // Calculate distance to determine animation speed
    const distance = getDistance(currentPoint, nextPoint);
    // Adjust speed based on distance - longer distances move faster, shorter distances move slower
    // Base speed: 0.002 per frame, adjusted by distance (normalized to 0.5-2km range)
    const baseSpeed = 0.002;
    const distanceFactor = Math.min(Math.max(distance / 1.0, 0.5), 2.0); // Normalize between 0.5 and 2.0
    const speed = baseSpeed * distanceFactor;
    
    // Increase progress (0 to 1) with smooth easing
    progressRef.current += speed;

    // If we've reached the next point, move to next segment
    if (progressRef.current >= 1) {
      progressRef.current = 0;
      routeIndexRef.current = nextIndex;
      
      // Update address and timestamp when reaching a new point
      setCurrentLocation(prev => ({
        ...prev,
        address: nextPoint.address || prev.address,
        timestamp: nextPoint.point_time 
          ? new Date(nextPoint.point_time).toLocaleTimeString()
          : new Date().toLocaleTimeString()
      }));
    }

    // Apply easing to progress for smoother animation
    const easedProgress = easeInOutCubic(progressRef.current);
    
    // Calculate interpolated position with easing
    const interpolatedLat = lerp(currentPoint.lat, nextPoint.lat, easedProgress);
    const interpolatedLng = lerp(currentPoint.lng, nextPoint.lng, easedProgress);

    // Calculate bearing (direction) for rotation
    const targetBearing = getBearing(currentPoint, nextPoint);
    
    // Smooth rotation transition (interpolate between current and target bearing)
    // Handle angle wrapping (e.g., 359° to 1° should be 2° change, not 358°)
    let angleDiff = targetBearing - currentBearingRef.current;
    if (angleDiff > 180) angleDiff -= 360;
    if (angleDiff < -180) angleDiff += 360;
    
    // Smoothly interpolate rotation (0.1 = 10% per frame for smooth rotation)
    const rotationSpeed = 0.15;
    currentBearingRef.current = (currentBearingRef.current + angleDiff * rotationSpeed + 360) % 360;

    // Update marker position smoothly
    vehicleMarkerRef.current.setPosition({
      lat: interpolatedLat,
      lng: interpolatedLng,
    });

    // Update marker rotation smoothly with transition
    if (hasSetRotation(vehicleMarkerRef.current)) {
      vehicleMarkerRef.current.setRotation(currentBearingRef.current);
    } else {
      // Fallback: Apply CSS transform rotation with smooth transition
      const element = vehicleMarkerRef.current.getElement ? vehicleMarkerRef.current.getElement() : null;
      if (element) {
        const img = element.querySelector('img');
        const targetElement = img || element;
        if (targetElement) {
          targetElement.style.transition = 'transform 0.1s ease-out';
          targetElement.style.transform = `rotate(${currentBearingRef.current}deg)`;
        }
      }
    }

    // Update current location state (throttled for performance)
    const now = Date.now();
    if (!vehicleMarkerRef.current.lastUpdate || now - vehicleMarkerRef.current.lastUpdate > 50) {
      vehicleMarkerRef.current.lastUpdate = now;
      
      // Calculate speed based on distance and time (dummy calculation - will use API speed when available)
      const pointDistance = getDistance(currentPoint, nextPoint);
      const estimatedSpeed = Math.min(60, Math.max(20, pointDistance * 25)); // More realistic speed range

      setCurrentLocation(prev => ({
        ...prev,
        lat: interpolatedLat,
        lng: interpolatedLng,
        speed: Math.round(estimatedSpeed),
        status: "Moving",
        timestamp: new Date().toLocaleTimeString(),
      }));
    }

    // Smooth camera follow - update more frequently for smoother tracking
    // Mappls panTo uses [longitude, latitude] format
    if (!mapInstance.current.lastPan || now - mapInstance.current.lastPan > 100) {
      mapInstance.current.lastPan = now;
      // Use panTo with shorter duration for smoother camera movement
      mapInstance.current.panTo(
        [interpolatedLng, interpolatedLat],
        { duration: 150 } // Shorter duration for smoother, more responsive camera
      );
    }

    // Continue animation
    animationFrameRef.current = requestAnimationFrame(animateVehicle);
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      // Pause animation
      isPlayingRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setIsPlaying(false);
      setCurrentLocation(prev => ({ ...prev, status: "Paused" }));
    } else {
      // Resume animation
      isPlayingRef.current = true;
      setIsPlaying(true);
      setCurrentLocation(prev => ({ ...prev, status: "Moving" }));
      animationFrameRef.current = requestAnimationFrame(animateVehicle);
    }
  };

  const stopTracking = () => {
    // Cancel animation frame
    isPlayingRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
    routeIndexRef.current = 0;
    progressRef.current = 0;
    
    if (route.length > 0) {
    const startPoint = route[0];
    if (vehicleMarkerRef.current) {
      vehicleMarkerRef.current.setPosition({
        lat: startPoint.lat,
        lng: startPoint.lng,
      });
    }
    if (mapInstance.current) {
        // Mappls panTo uses [longitude, latitude] format
      mapInstance.current.panTo(
          [startPoint.lng, startPoint.lat],
        { duration: 800 }
      );
    }
    setCurrentLocation({
      lat: startPoint.lat,
      lng: startPoint.lng,
      speed: 0,
      status: "Stopped",
        timestamp: startPoint.point_time 
          ? new Date(startPoint.point_time).toLocaleTimeString()
          : new Date().toLocaleTimeString(),
        address: startPoint.address || "Location not available"
      });
    }
  };

  const refreshLocation = () => {
    fetchLocations(true); // Show loading on manual refresh
  };

  // Center map on selected vehicle's location
  const centerOnVehicleLocation = () => {
    if (!selectedDeviceId || !mapInstance.current) return;
    
    // Find the selected device's location from locationsData
    const deviceLocation = locationsData.find(loc => loc.device_id === selectedDeviceId);
    if (deviceLocation) {
      const vehiclePos = { lat: deviceLocation.latitude, lng: deviceLocation.longitude };
      
      // Center map on vehicle location with smooth animation
      // Mappls uses [longitude, latitude] format
      mapInstance.current.setCenter([vehiclePos.lng, vehiclePos.lat], {
        duration: 800
      });
      
      // Set appropriate zoom level
      mapInstance.current.setZoom(14);
    } else if (route.length > 0) {
      // Fallback: use latest route point if location data not found
      const latestLocation = route[route.length - 1];
      mapInstance.current.setCenter([latestLocation.lng, latestLocation.lat], {
        duration: 800
      });
      mapInstance.current.setZoom(14);
    }
  };

  // Handle device selection change
  const handleDeviceChange = (deviceId) => {
    if (deviceId === '') {
      // Clear selection - show all vehicles
      setSelectedDeviceId(null);
      setRoute([]);
      // Stop animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      isPlayingRef.current = false;
      setIsPlaying(false);
      
      // Close mobile card when clearing selection
      if (isMobile) {
        setIsMobileCardOpen(false);
        setCardState('normal');
        setCardHeight(40);
        cardHeightRef.current = 40;
      }
      
      // Show all path polylines again (they were hidden for selected device)
      if (mapInstance.current) {
        devicePolylinesRef.current.forEach((polyline, devId) => {
          // Skip shadow polylines (they're handled with their main polyline)
          if (devId.endsWith('_shadow')) return;
          
          try {
            if (polyline && typeof polyline.setOptions === 'function') {
              polyline.setOptions({ strokeOpacity: 0.85 });
            } else if (typeof polyline.setMap === 'function') {
              polyline.setMap(mapInstance.current);
            }
            // Also show shadow if it exists
            const shadowPolyline = devicePolylinesRef.current.get(`${devId}_shadow`);
            if (shadowPolyline && typeof shadowPolyline.setOptions === 'function') {
              shadowPolyline.setOptions({ strokeOpacity: 0.4 });
            } else if (shadowPolyline && typeof shadowPolyline.setMap === 'function') {
              shadowPolyline.setMap(mapInstance.current);
            }
          } catch (error) {
          }
        });
      }
      
      // Set flag to fit bounds when selection is cleared (only once, not on every data update)
      shouldFitBoundsRef.current = true;
      
      // Update markers to show all (no selection)
      if (mapInstance.current && locationsData.length > 0) {
        createAllVehicleMarkers(mapInstance.current, locationsData);
        // fitMapToAllVehicles will be called by useEffect when shouldFitBoundsRef is true
      }
      return;
    }
    
    setSelectedDeviceId(deviceId);
    updateRouteForDevice(deviceId, locationsData);
    // Reset animation
    routeIndexRef.current = 0;
    progressRef.current = 0;
    
    // Hide the path polyline for selected device (blue route polyline will be shown instead)
    if (mapInstance.current) {
      const selectedPolyline = devicePolylinesRef.current.get(deviceId);
      if (selectedPolyline) {
        try {
          if (typeof selectedPolyline.setOptions === 'function') {
            selectedPolyline.setOptions({ strokeOpacity: 0 });
          } else if (typeof selectedPolyline.setMap === 'function') {
            selectedPolyline.setMap(null);
          }
        } catch (error) {
        }
      }
      // Also hide shadow polyline if it exists
      const shadowPolyline = devicePolylinesRef.current.get(`${deviceId}_shadow`);
      if (shadowPolyline) {
        try {
          if (typeof shadowPolyline.setOptions === 'function') {
            shadowPolyline.setOptions({ strokeOpacity: 0 });
          } else if (typeof shadowPolyline.setMap === 'function') {
            shadowPolyline.setMap(null);
          }
        } catch (error) {
        }
      }
    }
    
    // Set flag to center on route when device is first selected (only once, not on every data update)
    shouldCenterOnRouteRef.current = true;
    
    // Close device selector card when vehicle is selected (mobile)
    if (isMobile) {
      setIsDeviceCardOpen(false);
    }
    
    // Don't create all markers here - let the useEffect handle it when route updates
    // The route useEffect will call setupMapWithRoute which creates the single vehicle marker
    
    if (isPlaying) {
      // Restart animation will be handled in setupMapWithRoute
    }
  };

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Refs for drag handling to avoid stale closures
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const cardHeightRef = useRef(40);

  // Drag handling functions
  const handleDragStart = (e) => {
    isDraggingRef.current = true;
    dragStartYRef.current = e.touches ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setDragStartY(dragStartYRef.current);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = currentY - dragStartYRef.current;
    const newHeight = Math.max(20, Math.min(80, 40 - (deltaY / window.innerHeight) * 100));
    cardHeightRef.current = newHeight;
    setCardHeight(newHeight);
  };

  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    
    // Determine final state based on height
    const finalHeight = cardHeightRef.current;
    if (finalHeight > 60) {
      setCardState('expanded');
      setCardHeight(75);
      cardHeightRef.current = 75;
    } else if (finalHeight < 30) {
      setCardState('collapsed');
      setCardHeight(20);
      cardHeightRef.current = 20;
    } else {
      setCardState('normal');
      setCardHeight(40);
      cardHeightRef.current = 40;
    }
  };

  // Card state management functions
  const expandMobileCard = () => {
    if (cardState === 'expanded') {
      setCardState('normal');
      setCardHeight(40);
    } else {
      setCardState('expanded');
      setCardHeight(75);
    }
  };

  const collapseMobileCard = () => {
    if (cardState === 'collapsed') {
      setCardState('normal');
      setCardHeight(40);
    } else {
      setCardState('collapsed');
      setCardHeight(20);
    }
  };

  const closeMobileCard = () => {
    setIsMobileVehicleCardOpen(false);
    setCardState('normal');
    setCardHeight(40);
    cardHeightRef.current = 40;
  };

  // Global drag event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDraggingRef.current) {
        handleDragMove(e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDraggingRef.current) {
        handleDragEnd();
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchmove', handleGlobalMouseMove, { passive: false });
    document.addEventListener('touchend', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalMouseMove);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  // Fetch locations on component mount and set up auto-refresh
  useEffect(() => {
    fetchLocations(true); // Show loading on initial load
    
    // Set up auto-refresh every 3 seconds to get new location updates (more responsive)
    // Don't show loading spinner on auto-refresh to avoid interrupting animations
    refreshIntervalRef.current = setInterval(() => {
      fetchLocations(false); // Don't show loading on auto-refresh
    }, 3000); // Refresh every 3 seconds for more responsive updates
    
    // Cleanup interval on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      
      // Cancel all active animations
      markerAnimationsRef.current.forEach((animation) => {
        if (animation.frameId) {
          cancelAnimationFrame(animation.frameId);
        }
      });
      markerAnimationsRef.current.clear();
    };
  }, []);

  // Initialize map once
  useEffect(() => {
    if (!window.mappls) {
      return;
    }

    // Only initialize map if not already initialized
    if (mapInstance.current) {
      return;
    }

    // Default center (will be updated when route is available)
    const defaultCenter = route.length > 0 
      ? [route[0].lat, route[0].lng]
      : [17.385044, 78.486671]; // Default to Hyderabad

    /* 🗺️ Initialize map */
    // Mappls uses [longitude, latitude] format for center
    const mapCenter = route.length > 0 
      ? [route[0].lng, route[0].lat]
      : [78.486671, 17.385044]; // Default to Hyderabad [lng, lat]
    
    const map = new window.mappls.Map(mapRef.current, {
      center: mapCenter,
      zoom: 12,
      zoomControl: true,
    });

    mapInstance.current = map;

    map.on("load", () => {
      // If route is available, set up marker and route
      if (route.length > 0) {
        setupMapWithRoute(map);
      }
    });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (routePolylineRef.current) {
        try {
          // Check if remove method exists before calling
          if (typeof routePolylineRef.current.remove === 'function') {
            routePolylineRef.current.remove();
          }
        } catch (error) {
        }
        routePolylineRef.current = null;
      }
      // Clean up start point marker
      if (startPointMarkerRef.current) {
        try {
          if (typeof startPointMarkerRef.current.remove === 'function') {
            startPointMarkerRef.current.remove();
          }
        } catch (error) {
        }
        startPointMarkerRef.current = null;
      }
      // Clean up all vehicle markers
      allVehicleMarkersRef.current.forEach(marker => {
        try {
          if (marker && typeof marker.remove === 'function') {
            marker.remove();
          }
        } catch (error) {
        }
      });
      allVehicleMarkersRef.current = [];
      
      // Clean up all device polylines (including shadows)
      devicePolylinesRef.current.forEach((polyline, deviceId) => {
        try {
          if (polyline && typeof polyline.remove === 'function') {
            polyline.remove();
          }
        } catch (error) {
        }
      });
      devicePolylinesRef.current.clear();
      devicePathsRef.current.clear();
    };
  }, []); // Only run once on mount

  // Setup map with route data
  const setupMapWithRoute = (map) => {
    
    if (!map || route.length === 0 || !selectedDeviceId) {
      return;
    }

    /* 🟢 Create start point marker with "S" label FIRST (before other operations) */
    // Remove existing start point marker if it exists
    if (startPointMarkerRef.current) {
      try {
        if (typeof startPointMarkerRef.current.remove === 'function') {
          startPointMarkerRef.current.remove();
        }
      } catch (error) {
      }
      startPointMarkerRef.current = null;
    }

    // Create start point marker at the first route point
    if (route.length > 0) {
      try {
        const startPoint = route[0];
        const startIconUrl = getStartPointIcon();
        startPointMarkerRef.current = new window.mappls.Marker({
          map,
          position: { lat: startPoint.lat, lng: startPoint.lng },
          icon: startIconUrl,
          width: 36,
          height: 36,
          anchor: [18, 18],
          zIndex: 10,
        });
      } catch (error) {
      }
    }

      /* 🟦 Draw route */
    // Remove existing polyline if it exists
    if (routePolylineRef.current) {
      try {
        // Check if remove method exists before calling
        if (typeof routePolylineRef.current.remove === 'function') {
          routePolylineRef.current.remove();
        }
        // If remove doesn't exist, the old polyline will be replaced by the new one
      } catch (error) {
        // If removal fails, just continue - new polyline will replace it
      }
      routePolylineRef.current = null;
    }
    
    // Create new polyline (only if route has more than 1 point)
    if (route.length > 1) {
      try {
        routePolylineRef.current = new window.mappls.Polyline({
          map,
          path: route,
          strokeColor: "#2563eb",
          strokeOpacity: 0.9,
          strokeWeight: 6,
        });
      } catch (error) {
      }
    } else {
    }

    /* 🚗 Vehicle marker - Use car SVG icon for selected vehicle */
    // Remove existing vehicle marker if it exists
    if (vehicleMarkerRef.current) {
      try {
        if (typeof vehicleMarkerRef.current.remove === 'function') {
          vehicleMarkerRef.current.remove();
        }
      } catch (error) {
      }
      vehicleMarkerRef.current = null;
    }

    // Use the latest location (last point in route) for the marker position
    const latestRoutePoint = route.length > 0 ? route[route.length - 1] : route[0];
    
    // Calculate initial bearing - if we have multiple points, use direction from second-to-last to last
      const initialBearing = route.length > 1 
      ? getBearing(route[route.length - 2], route[route.length - 1])
        : 0;
      currentBearingRef.current = initialBearing;
      
      const carIconUrl = getCarIconRed();
    const vehicleInfo = deviceVehicleMap.get(selectedDeviceId);
    const vehicleNumber = vehicleInfo?.vehicle_number || '';
    const vehicleName = vehicleInfo?.vehicle_name || '';
    
    // Get full location data for this device (use latest location data)
    const deviceFullLocations = locationsData
      .filter(loc => loc.device_id === selectedDeviceId)
      .sort((a, b) => new Date(b.point_time) - new Date(a.point_time));
    const fullLocationData = deviceFullLocations.length > 0 ? deviceFullLocations[0] : null;
      
      vehicleMarkerRef.current = new window.mappls.Marker({
        map,
      position: latestRoutePoint,
        icon: carIconUrl,
        width: 32,
        height: 64,
        anchor: [16, 32], // Center anchor point
        // Don't show popup on desktop - show detailed card instead
        popupHtml: isMobile ? `
          <div style="font-size:12px">
          <b>${vehicleNumber || vehicleName || 'Live Vehicle'}</b><br/>
            Speed: ${currentLocation.speed} km/h<br/>
            Status: ${currentLocation.status}
          </div>
        ` : undefined, // No popup on desktop
      });

      // Store device ID and full data reference with marker
      vehicleMarkerRef.current._deviceId = selectedDeviceId;
      if (fullLocationData) {
        vehicleMarkerRef.current._fullLocationData = fullLocationData;
      }
      
      // Store initial position
      previousPositionsRef.current.set(selectedDeviceId, {
        lat: latestRoutePoint.lat,
        lng: latestRoutePoint.lng
      });

      // Add click event listener to show detailed card (desktop only)
      if (!isMobile) {
        // Try using mappls event system first
        if (vehicleMarkerRef.current && typeof vehicleMarkerRef.current.addEventListener === 'function') {
          vehicleMarkerRef.current.addEventListener('click', (e) => {
            e?.stopPropagation?.();
            if (vehicleMarkerRef.current._fullLocationData) {
              setSelectedMarkerData(vehicleMarkerRef.current._fullLocationData);
            }
          });
        }
        
        // Fallback: try to add click handler via DOM element
        setTimeout(() => {
          try {
            const element = vehicleMarkerRef.current?.getElement ? vehicleMarkerRef.current.getElement() : null;
            if (element) {
              element.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault(); // Prevent default popup
                if (vehicleMarkerRef.current._fullLocationData) {
                  setSelectedMarkerData(vehicleMarkerRef.current._fullLocationData);
                }
              });
              element.style.cursor = 'pointer';
            }
          } catch (error) {
          }
        }, 200);
      }

      // Apply initial rotation
      if (hasSetRotation(vehicleMarkerRef.current)) {
        vehicleMarkerRef.current.setRotation(initialBearing);
      } else {
        // Fallback: Apply CSS transform rotation
        setTimeout(() => {
          const element = vehicleMarkerRef.current?.getElement ? vehicleMarkerRef.current.getElement() : null;
          if (element) {
            const img = element.querySelector('img');
            if (img) {
              img.style.transform = `rotate(${initialBearing}deg)`;
            } else {
              element.style.transform = `rotate(${initialBearing}deg)`;
            }
          }
        }, 100);
      }

      /* ⏱️ Start smooth live animation using requestAnimationFrame */
      progressRef.current = 0;
    routeIndexRef.current = 0;
    if (isPlayingRef.current && !animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(animateVehicle);
    }
  };

  // Update map when route or selection changes (but not on every data update)
  useEffect(() => {
    if (mapInstance.current) {
      if (route.length > 0 && selectedDeviceId) {
        // Vehicle selected - show route and animated car
        setupMapWithRoute(mapInstance.current);
        
        // Hide all simple markers when showing selected vehicle
        allVehicleMarkersRef.current.forEach(marker => {
          try {
            if (marker && typeof marker.remove === 'function') {
              marker.remove();
            }
          } catch (error) {
          }
        });
        allVehicleMarkersRef.current = [];
        
        // Only center on selected vehicle's location when explicitly requested (not on every data update)
        // This happens when device is first selected or when "Vehicle Location" button is clicked
        if (shouldCenterOnRouteRef.current) {
          const latestLocation = route.length > 0 ? route[route.length - 1] : route[0];
          
          // Center on selected vehicle's latest location without zooming in too much
          // Mappls uses [longitude, latitude] format for setCenter
          if (latestLocation) {
            mapInstance.current.setCenter([latestLocation.lng, latestLocation.lat], {
              duration: 800
            });
            // Set a moderate zoom level (12) to show the vehicle without zooming in too much
            mapInstance.current.setZoom(12);
          }
          shouldCenterOnRouteRef.current = false; // Reset flag after use
        }
      } else if (!selectedDeviceId && locationsData.length > 0) {
        // No selection - show all vehicles
        // Clear the animated vehicle marker and route
        if (vehicleMarkerRef.current) {
          try {
            if (typeof vehicleMarkerRef.current.remove === 'function') {
              vehicleMarkerRef.current.remove();
            }
          } catch (error) {
          }
          vehicleMarkerRef.current = null;
        }
        if (routePolylineRef.current) {
          try {
            if (typeof routePolylineRef.current.remove === 'function') {
              routePolylineRef.current.remove();
            }
          } catch (error) {
          }
        routePolylineRef.current = null;
      }
        
        // Remove start point marker
        if (startPointMarkerRef.current) {
          try {
            if (typeof startPointMarkerRef.current.remove === 'function') {
              startPointMarkerRef.current.remove();
            }
          } catch (error) {
          }
          startPointMarkerRef.current = null;
        }
        
        // Show all vehicle markers (update markers without changing camera)
        createAllVehicleMarkers(mapInstance.current, locationsData);
        
        // Only fit bounds when explicitly requested (when selection is cleared, not on every data update)
        if (shouldFitBoundsRef.current) {
          fitMapToAllVehicles(mapInstance.current, locationsData);
          shouldFitBoundsRef.current = false; // Reset flag after use
        }
      }
    }
  }, [route, selectedDeviceId]); // Removed locationsData from dependencies to prevent reset on every update

  // Update markers when devices change (but don't reset camera)
  // This only updates markers, doesn't change the map view
  useEffect(() => {
    if (mapInstance.current && locationsData.length > 0 && devices.length > 0) {
      // Only update markers if no device is selected (showing all)
      // Don't fit bounds here - only update markers smoothly
      if (!selectedDeviceId) {
        createAllVehicleMarkers(mapInstance.current, locationsData);
        // Don't call fitMapToAllVehicles here - it will reset the view on every update
        // Only fit bounds when explicitly requested (when selection is cleared)
      }
    }
  }, [devices.length, selectedDeviceId]); // Removed locationsData to prevent reset on every update

  // Auto-open mobile card when vehicle is selected and close device selector
  useEffect(() => {
    if (isMobile && selectedDeviceId) {
      setIsMobileVehicleCardOpen(true);
      setIsDeviceCardOpen(false); // Close device selector when vehicle is selected
      setIsMobileDeviceDropdownOpen(false); // Close dropdown when vehicle is selected
      setCardState('normal');
      setCardHeight(40);
      cardHeightRef.current = 40;
    } else if (isMobile && !selectedDeviceId) {
      // Close mobile vehicle card when selection is cleared
      setIsMobileVehicleCardOpen(false);
    }
  }, [selectedDeviceId, isMobile]);

  // Close mobile device dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileDeviceDropdownOpen && !event.target.closest('.mobile-device-dropdown-container')) {
        setIsMobileDeviceDropdownOpen(false);
      }
    };

    if (isMobileDeviceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileDeviceDropdownOpen]);

  // Close desktop device dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDesktopDeviceDropdownOpen && !event.target.closest('.desktop-device-dropdown-container')) {
        setIsDesktopDeviceDropdownOpen(false);
      }
    };

    if (isDesktopDeviceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDesktopDeviceDropdownOpen]);

  // Close detailed marker card when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedMarkerData && !isMobile && !event.target.closest('.desktop-marker-detail-card')) {
        setSelectedMarkerData(null);
      }
    };

    if (selectedMarkerData && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedMarkerData, isMobile]);

  return (
    <div className="relative w-full h-screen bg-background">
      {/* Mobile Card Styles */}
      <style>{`
        @media (max-width: 768px) {
          .mobile-map-container {
            height: 100vh !important;
            min-height: 100vh !important;
            transition: height 0.3s ease;
          }
          
          .mobile-map-container.card-open {
            height: 60vh !important;
            min-height: 60vh !important;
          }
          
          .mobile-vehicle-card-container {
            height: 40vh !important;
            min-height: 40vh !important;
            max-height: 40vh !important;
            overflow-y: auto;
            overflow-x: hidden;
            transition: transform 0.3s ease, height 0.3s ease;
            transform: translateY(0);
            will-change: transform, height;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 20;
            -webkit-overflow-scrolling: touch;
            border-radius: 16px 16px 0 0;
            box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.3);
          }
          
          .mobile-vehicle-card-container.expanded {
            height: 75vh !important;
            min-height: 75vh !important;
            max-height: 75vh !important;
            transform: translateY(0);
            z-index: 20;
            box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.3);
          }
          
          .mobile-vehicle-card-container.collapsed {
            height: 20vh !important;
            min-height: 20vh !important;
            max-height: 20vh !important;
            transform: translateY(0);
            box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.2);
          }
          
          .mobile-vehicle-card-container.normal {
            height: 40vh !important;
            min-height: 40vh !important;
            max-height: 40vh !important;
            transform: translateY(0) !important;
          }

          .mobile-vehicle-card-container.dragging {
            transition: none !important;
            z-index: 20;
          }

          .mobile-card-drag-handle {
            height: 4px;
            background: #e5e7eb;
            border-radius: 2px;
            margin: 8px auto;
            width: 40px;
            cursor: grab;
            transition: none;
          }

          .mobile-card-drag-handle:hover {
            background: #9ca3af;
          }

          .mobile-card-drag-handle:active {
            cursor: grabbing;
          }

          .map-blur-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(8px);
            z-index: 5;
            opacity: 0;
            transition: opacity 0.3s ease;
            pointer-events: none;
          }

          .map-blur-overlay.active {
            opacity: 1;
            pointer-events: auto;
          }
          
          .mobile-vehicle-card-container::-webkit-scrollbar {
            width: 6px;
          }
          
          .mobile-vehicle-card-container::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 3px;
          }
          
          .mobile-vehicle-card-container::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
          }
          
          .mobile-vehicle-card-container::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          
          @keyframes glow-pulse {
            0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.3); }
            50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.6), 0 0 30px rgba(59, 130, 246, 0.4); }
          }
          
          @keyframes slideInUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes bounceIn {
            0% {
              transform: scale(0.3);
              opacity: 0;
            }
            50% {
              transform: scale(1.05);
            }
            70% {
              transform: scale(0.9);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          .floating-icon {
            animation: float 3s ease-in-out infinite;
          }
          
          .shimmer-text {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 2s infinite;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          
          .glow-card {
            animation: glow-pulse 2s ease-in-out infinite;
          }
          
          .mobile-vehicle-card-container {
            animation: slideInUp 0.3s ease-out;
          }
          
          .mobile-device-dropdown-container > div:last-child {
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
            scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
          }
          
          .mobile-device-dropdown-container > div:last-child::-webkit-scrollbar {
            width: 4px;
          }
          
          .mobile-device-dropdown-container > div:last-child::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .mobile-device-dropdown-container > div:last-child::-webkit-scrollbar-thumb {
            background: rgba(156, 163, 175, 0.5);
            border-radius: 2px;
          }
          
          .mobile-device-dropdown-container > div:last-child::-webkit-scrollbar-thumb:hover {
            background: rgba(156, 163, 175, 0.7);
          }
          
          .desktop-device-dropdown-container > div:last-child {
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
            scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
          }
          
          .desktop-device-dropdown-container > div:last-child::-webkit-scrollbar {
            width: 6px;
          }
          
          .desktop-device-dropdown-container > div:last-child::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .desktop-device-dropdown-container > div:last-child::-webkit-scrollbar-thumb {
            background: rgba(156, 163, 175, 0.5);
            border-radius: 3px;
          }
          
          .desktop-device-dropdown-container > div:last-child::-webkit-scrollbar-thumb:hover {
            background: rgba(156, 163, 175, 0.7);
          }
        }
      `}</style>
      {/* Loading Overlay */}
      {isLoadingLocations && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="p-6">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading locations...</p>
            </div>
          </Card>
        </div>
      )}

      {/* Error Overlay */}
      {error && !isLoadingLocations && (
        <div className="absolute top-2 left-2 right-2 md:top-4 md:left-1/2 md:right-auto md:transform md:-translate-x-1/2 z-20 md:max-w-md">
          <Card className="border-2 border-destructive shadow-xl">
            <CardContent className="pt-4 pb-4 px-4 md:pt-6 md:pb-6 md:px-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 md:h-5 md:w-5 text-destructive flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-sm font-semibold text-destructive">Error loading locations</p>
                  <p className="text-xs md:text-xs text-muted-foreground mt-1 break-words">{error}</p>
                </div>
                <Button
                  variant="ghost"
                  size="default"
                  onClick={fetchLocations}
                  className="h-10 w-10 md:h-8 md:w-auto md:px-3 flex-shrink-0 touch-manipulation active:scale-95 transition-transform"
                  title="Retry"
                >
                  <RefreshCw className="h-5 w-5 md:h-4 md:w-4" />
                  <span className="hidden md:inline ml-2">Retry</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Vehicles Popup */}
      {!isLoadingLocations && !error && devices.length === 0 && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 md:top-6">
          <Card className="border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 dark:border-amber-700 shadow-lg backdrop-blur-sm animate-in fade-in slide-in-from-top-2 duration-300">
            <CardContent className="pt-3 pb-3 px-4 md:pt-3.5 md:pb-3.5 md:px-5">
              <div className="flex items-center gap-2.5 md:gap-3">
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-4 w-4 md:h-4.5 md:w-4.5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs md:text-sm font-semibold text-amber-900 dark:text-amber-200">No Vehicles Found</p>
                  <p className="text-[10px] md:text-xs text-amber-700 dark:text-amber-300 mt-0.5">No vehicles are currently available on the map</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Map Container */}
      <div
        ref={mapRef}
        id="live-map"
        className={`w-full h-full ${isMobile && selectedDeviceId && isMobileVehicleCardOpen ? 'mobile-map-container card-open' : ''}`}
        style={{
          borderRadius: "12px",
        }}
      />

      {/* Blur overlay for mobile when card is expanded */}
      {isMobile && cardState === 'expanded' && selectedDeviceId && isMobileVehicleCardOpen && (
        <div className="map-blur-overlay active" style={{ borderRadius: "12px" }}></div>
      )}

      {/* Device Selector - Mobile Icon Button */}
      {devices.length > 0 && (
        <>
          {/* Mobile: Icon-only buttons */}
          <div className="md:hidden absolute top-2 right-2 z-20 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeviceCardOpen(!isDeviceCardOpen)}
              className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark: h-9 w-9 p-0 touch-manipulation"
              title="Select Device"
            >
              <MapPin className="h-4 w-4 text-gray-700" />
            </Button>
            {selectedDeviceId && (
              <Button
                variant="outline"
                size="sm"
                onClick={centerOnVehicleLocation}
                className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark: h-9 w-9 p-0 touch-manipulation"
                title="Vehicle Location"
              >
                <Navigation className="h-4 w-4 text-gray-700" />
              </Button>
            )}
          </div>
          
          {/* Mobile: Card that appears when button is clicked */}
          {isDeviceCardOpen && (
            <div className="md:hidden absolute top-12 right-2 z-20 w-[calc(100%-1rem)] max-w-[260px]">
        <Card className="border-1 border-muted-foreground/20 shadow-lg bg-background/95 backdrop-blur-sm">
                <CardHeader className="pb-1.5 pt-2 px-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-primary" />
                      Select Vehicle
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsDeviceCardOpen(false);
                        setIsMobileDeviceDropdownOpen(false);
                      }}
                      className="h-5 w-5 p-0 text-xs"
                    >
                      ×
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 px-3 pb-3">
                  {/* Search Input */}
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search vehicle, IMEI..."
                      value={deviceSearchQuery}
                      onChange={(e) => setDeviceSearchQuery(e.target.value)}
                      className="pl-9 h-9 text-xs border-2 rounded-md bg-background touch-manipulation"
                    />
                  </div>
                  
                  {/* Custom Device Dropdown */}
                  <div className="relative mobile-device-dropdown-container">
                    <Button
                      variant="outline"
                      onClick={() => setIsMobileDeviceDropdownOpen(!isMobileDeviceDropdownOpen)}
                      className="w-full justify-between h-9 text-xs border-2 rounded-md bg-background font-medium touch-manipulation px-2.5"
                    >
                      <span className="truncate flex-1 text-left">
                        {selectedDeviceId 
                          ? (() => {
                              const selectedDevice = devices.find(d => d.device_id === selectedDeviceId);
                              if (selectedDevice) {
                                const vehicleNumber = selectedDevice.vehicle_number || '';
                                const vehicleName = selectedDevice.vehicle_name || '';
                                const imeiNumber = selectedDevice.imei_number || '';
                                return vehicleNumber 
                                  ? `${vehicleNumber}${vehicleName ? ` - ${vehicleName}` : ''}`
                                  : vehicleName || imeiNumber || `${selectedDeviceId.substring(0, 8)}...`;
                              }
                              return 'Select Device';
                            })()
                          : 'Show All Vehicles'}
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 ml-2 flex-shrink-0 transition-transform ${isMobileDeviceDropdownOpen ? 'rotate-180' : ''}`} />
                    </Button>
                    
                    {/* Dropdown Menu */}
                    {isMobileDeviceDropdownOpen && (
                      <div className="absolute z-30 w-full mt-1 bg-background border-2 border-muted-foreground/20 rounded-md shadow-lg max-h-[200px] overflow-y-auto">
                        <div 
                          onClick={() => {
                            handleDeviceChange('');
                            setIsMobileDeviceDropdownOpen(false);
                          }}
                          className={`px-3 py-2.5 text-xs cursor-pointer touch-manipulation transition-colors ${
                            !selectedDeviceId 
                              ? 'bg-primary/10 text-primary font-semibold' 
                              : 'hover:bg-muted/50 text-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span>Show All Vehicles</span>
                          </div>
                        </div>
                        {devices
                          .filter(device => {
                            if (!deviceSearchQuery) return true;
                            const query = deviceSearchQuery.toLowerCase();
                            const vehicleNumber = device.vehicle_number || '';
                            const vehicleName = device.vehicle_name || '';
                            const imeiNumber = device.imei_number || '';
                            const deviceId = device.device_id || '';
                            return deviceId.toLowerCase().includes(query) || 
                                   vehicleNumber.toLowerCase().includes(query) ||
                                   vehicleName.toLowerCase().includes(query) ||
                                   imeiNumber.toLowerCase().includes(query);
                          })
                          .map(device => {
                            const deviceId = device.device_id;
                            const vehicleNumber = device.vehicle_number || '';
                            const vehicleName = device.vehicle_name || '';
                            const imeiNumber = device.imei_number || '';
                            const displayText = vehicleNumber 
                              ? `${vehicleNumber}${vehicleName ? ` - ${vehicleName}` : ''}`
                              : vehicleName || imeiNumber || `${deviceId.substring(0, 8)}...`;
                            const isSelected = deviceId === selectedDeviceId;
                            
                            return (
                              <div
                                key={deviceId}
                                onClick={() => {
                                  handleDeviceChange(deviceId);
                                  setIsMobileDeviceDropdownOpen(false);
                                }}
                                className={`px-3 py-2.5 text-xs cursor-pointer touch-manipulation transition-colors border-t border-muted-foreground/10 ${
                                  isSelected 
                                    ? 'bg-primary/10 text-primary font-semibold' 
                                    : 'hover:bg-muted/50 text-foreground'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-primary' : 'bg-muted-foreground/30'}`}></div>
                                  <span className="truncate">{displayText}</span>
                                </div>
                              </div>
                            );
                          })}
                        {devices.filter(device => {
                          if (!deviceSearchQuery) return false;
                          const query = deviceSearchQuery.toLowerCase();
                          const vehicleNumber = device.vehicle_number || '';
                          const vehicleName = device.vehicle_name || '';
                          const imeiNumber = device.imei_number || '';
                          const deviceId = device.device_id || '';
                          return deviceId.toLowerCase().includes(query) || 
                                 vehicleNumber.toLowerCase().includes(query) ||
                                 vehicleName.toLowerCase().includes(query) ||
                                 imeiNumber.toLowerCase().includes(query);
                        }).length === 0 && (
                          <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                            No devices found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Desktop: Buttons */}
          <div className="hidden md:flex absolute top-4 right-4 z-20 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeviceCardOpen(!isDeviceCardOpen)}
              className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark:"
            >
              <MapPin className="h-4 w-4 mr-2 text-gray-700" />
              {isDeviceCardOpen ? 'Hide' : 'Select'} Vehicle
            </Button>
            {selectedDeviceId && (
              <Button
                variant="outline"
                size="sm"
                onClick={centerOnVehicleLocation}
                className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark:"
                title="Center on Vehicle Location"
              >
                <Navigation className="h-4 w-4 mr-2 text-gray-700" />
                Vehicle Location
              </Button>
            )}
          </div>
          
          {/* Desktop: Card that appears when button is clicked */}
          {isDeviceCardOpen && (
            <div className="hidden md:block absolute top-16 right-4 z-20 w-full max-w-[280px]">
              <Card className="border-1 border-muted-foreground/20 shadow-lg bg-background/95 backdrop-blur-sm">
                <CardHeader className="pb-1.5 pt-2 px-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-primary" />
                      Select Vehicle
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsDeviceCardOpen(false);
                        setIsDesktopDeviceDropdownOpen(false);
                      }}
                      className="h-5 w-5 p-0 text-xs"
                    >
                      ×
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1.5 px-3 pb-2.5">
                  {/* Search Input */}
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search vehicle, IMEI..."
                      value={deviceSearchQuery}
                      onChange={(e) => setDeviceSearchQuery(e.target.value)}
                      className="pl-7 h-8 text-xs border-1 border-muted-foreground/20 rounded-md bg-background"
                    />
                  </div>
                  
                  {/* Custom Device Dropdown */}
                  <div className="relative desktop-device-dropdown-container">
                    <Button
                      variant="outline"
                      onClick={() => setIsDesktopDeviceDropdownOpen(!isDesktopDeviceDropdownOpen)}
                      className="w-full justify-between h-8 text-xs border-1 border-muted-foreground/20 rounded-md bg-background font-medium px-2"
                    >
                      <span className="truncate flex-1 text-left">
                        {selectedDeviceId 
                          ? (() => {
                              const selectedDevice = devices.find(d => d.device_id === selectedDeviceId);
                              if (selectedDevice) {
                                const vehicleNumber = selectedDevice.vehicle_number || '';
                                const vehicleName = selectedDevice.vehicle_name || '';
                                const imeiNumber = selectedDevice.imei_number || '';
                                return vehicleNumber 
                                  ? `${vehicleNumber}${vehicleName ? ` - ${vehicleName}` : ''}`
                                  : vehicleName || imeiNumber || `${selectedDeviceId.substring(0, 8)}...`;
                              }
                              return 'Select Device';
                            })()
                          : 'Show All Vehicles'}
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 ml-2 flex-shrink-0 transition-transform ${isDesktopDeviceDropdownOpen ? 'rotate-180' : ''}`} />
                    </Button>
                    
                    {/* Dropdown Menu */}
                    {isDesktopDeviceDropdownOpen && (
                      <div className="absolute z-30 w-full mt-1 bg-background border-1 border-muted-foreground/20 rounded-md shadow-lg max-h-[250px] overflow-y-auto">
                        <div 
                          onClick={() => {
                            handleDeviceChange('');
                            setIsDesktopDeviceDropdownOpen(false);
                          }}
                          className={`px-2.5 py-2 text-xs cursor-pointer transition-colors ${
                            !selectedDeviceId 
                              ? 'bg-primary/10 text-primary font-semibold' 
                              : 'hover:bg-muted/50 text-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span>Show All Vehicles</span>
                          </div>
                        </div>
                        {devices
                          .filter(device => {
                            if (!deviceSearchQuery) return true;
                            const query = deviceSearchQuery.toLowerCase();
                            const vehicleNumber = device.vehicle_number || '';
                            const vehicleName = device.vehicle_name || '';
                            const imeiNumber = device.imei_number || '';
                            const deviceId = device.device_id || '';
                            return deviceId.toLowerCase().includes(query) || 
                                   vehicleNumber.toLowerCase().includes(query) ||
                                   vehicleName.toLowerCase().includes(query) ||
                                   imeiNumber.toLowerCase().includes(query);
                          })
                          .map(device => {
                            const deviceId = device.device_id;
                            const vehicleNumber = device.vehicle_number || '';
                            const vehicleName = device.vehicle_name || '';
                            const imeiNumber = device.imei_number || '';
                            const displayText = vehicleNumber 
                              ? `${vehicleNumber}${vehicleName ? ` - ${vehicleName}` : ''}`
                              : vehicleName || imeiNumber || `${deviceId.substring(0, 8)}...`;
                            const isSelected = deviceId === selectedDeviceId;
                            
                            return (
                              <div
                                key={deviceId}
                                onClick={() => {
                                  handleDeviceChange(deviceId);
                                  setIsDesktopDeviceDropdownOpen(false);
                                }}
                                className={`px-2.5 py-2 text-xs cursor-pointer transition-colors border-t border-muted-foreground/10 ${
                                  isSelected 
                                    ? 'bg-primary/10 text-primary font-semibold' 
                                    : 'hover:bg-muted/50 text-foreground'
                                }`}
                              >
                                <div className="flex items-center gap-1.5">
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-primary' : 'bg-muted-foreground/30'}`}></div>
                                  <span className="truncate">{displayText}</span>
                                </div>
                              </div>
                            );
                          })}
                        {devices.filter(device => {
                          if (!deviceSearchQuery) return false;
                          const query = deviceSearchQuery.toLowerCase();
                          const vehicleNumber = device.vehicle_number || '';
                          const vehicleName = device.vehicle_name || '';
                          const imeiNumber = device.imei_number || '';
                          const deviceId = device.device_id || '';
                          return deviceId.toLowerCase().includes(query) || 
                                 vehicleNumber.toLowerCase().includes(query) ||
                                 vehicleName.toLowerCase().includes(query) ||
                                 imeiNumber.toLowerCase().includes(query);
                        }).length === 0 && (
                          <div className="px-2.5 py-3 text-xs text-muted-foreground text-center">
                            No devices found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Back Button - Mobile */}
      <div className="md:hidden absolute top-2 left-2 z-20">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(getBackPath())}
          className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark: h-9 w-9 p-0 touch-manipulation"
          title="Back to Trip History"
        >
          <ArrowLeft className="h-4 w-4 text-gray-700" />
        </Button>
      </div>

      {/* Location Info Card - Mobile Icon Button */}
      <>
        {/* Mobile: Icon-only button */}
        <div className="md:hidden absolute top-2 left-12 z-20">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMobileCardOpen(!isMobileCardOpen)}
            className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark: h-9 w-9 p-0 touch-manipulation"
            title="Current Location"
          >
            <Navigation className="h-4 w-4 text-gray-700" />
          </Button>
        </div>
        
        {/* Mobile: Card that appears when button is clicked - Hide when vehicle card is open */}
        {isMobileCardOpen && !selectedDeviceId && (
          <div className="md:hidden absolute top-12 left-12 z-20 w-[calc(100%-3rem)] max-w-xs">
            <Card className="border-1 border-muted-foreground/20 shadow-lg bg-background/95 backdrop-blur-sm">
              <CardHeader className="pb-1.5 pt-2 px-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-primary" />
                      Current Location
                    </CardTitle>
                  <div className="flex items-center gap-1.5">
                      <Badge 
                        variant={currentLocation.status === "Moving" ? "default" : "secondary"}
                      className="flex items-center gap-1 text-[10px] h-5 px-1.5"
                      >
                      <Activity className={`h-2.5 w-2.5 ${currentLocation.status === "Moving" ? "animate-pulse" : ""}`} />
                        {currentLocation.status}
                      </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMobileCardOpen(false)}
                      className="h-5 w-5 p-0"
                    >
                      ×
                    </Button>
                    </div>
                  </div>
                </CardHeader>
              <CardContent className="space-y-1.5 pt-0 px-3 pb-2.5">
                {/* IMEI Number (if available) */}
                {selectedDeviceId && (() => {
                  const vehicleInfo = deviceVehicleMap.get(selectedDeviceId);
                  const imeiNumber = vehicleInfo?.imei_number;
                  return imeiNumber ? (
                    <div className="space-y-0.5 pb-1.5 border-b border-muted-foreground/10">
                      <div className="text-[10px] text-muted-foreground">IMEI Number</div>
                      <div className="text-xs font-mono font-medium break-all">{imeiNumber}</div>
                    </div>
                  ) : null;
                })()}
                  {/* Address */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Navigation className="h-3 w-3" />
                      <span className="font-medium">Address</span>
                    </div>
                  <p className="text-xs font-medium pl-4 line-clamp-2">{currentLocation.address}</p>
                  </div>

                  {/* Coordinates */}
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="space-y-0.5 p-1.5 bg-muted/30 rounded">
                      <div className="text-[10px] text-muted-foreground">Latitude</div>
                    <div className="text-xs font-mono font-medium break-all">{currentLocation.lat.toFixed(6)}</div>
                    </div>
                  <div className="space-y-0.5 p-1.5 bg-muted/30 rounded">
                      <div className="text-[10px] text-muted-foreground">Longitude</div>
                    <div className="text-xs font-mono font-medium break-all">{currentLocation.lng.toFixed(6)}</div>
                    </div>
                  </div>

                  {/* Speed */}
                <div className="flex items-center justify-between p-1.5 bg-primary/10 rounded-lg border-1 border-primary/20">
                  <div className="flex items-center gap-1">
                    <Gauge className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-medium">Speed</span>
                    </div>
                  <div className="text-lg font-bold text-primary">
                      {currentLocation.speed}
                    <span className="text-xs font-normal text-muted-foreground ml-0.5">km/h</span>
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t border-muted-foreground/10">
                  <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>Last Updated</span>
                    </div>
                    <span className="font-mono">{currentLocation.timestamp}</span>
                  </div>

                  {/* Control Buttons */}
                <div className="flex items-center gap-1.5 pt-1.5 border-t border-muted-foreground/10">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={togglePlayPause}
                    className={`flex-1 h-8 text-xs backdrop-blur-sm border-1 border-muted-foreground/20 touch-manipulation ${
                        isDarkMode 
                        ? "bg-white text-black  active:bg-gray-200" 
                        : "bg-background/95 hover:bg-background active:bg-muted"
                      }`}
                      title={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? (
                        <>
                        <Pause className="h-3.5 w-3.5 mr-1" />
                          Pause
                        </>
                      ) : (
                        <>
                        <Play className="h-3.5 w-3.5 mr-1" />
                          Play
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={stopTracking}
                    className={`flex-1 h-8 text-xs backdrop-blur-sm border-1 border-muted-foreground/20 touch-manipulation ${
                        isDarkMode 
                        ? "bg-white text-black  active:bg-gray-200" 
                        : "bg-background/95 hover:bg-background active:bg-muted"
                      }`}
                      title="Stop"
                    >
                    <Square className="h-3.5 w-3.5 mr-1" />
                      Stop
                    </Button>
                  </div>
                </CardContent>
            </Card>
            </div>
        )}
        
        {/* Desktop: Buttons */}
        <div className="hidden md:flex absolute top-4 left-4 z-20 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(getBackPath())}
            className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark:"
            title="Back to Trip History"
          >
            <ArrowLeft className="h-4 w-4 mr-2 text-gray-700" />
            Back
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMobileCardOpen(!isMobileCardOpen)}
            className="bg-white text-gray-900 border border-gray-200 shadow-md  dark:bg-white dark:text-gray-900 dark:"
          >
            <Navigation className="h-4 w-4 mr-2 text-gray-700" />
            {isMobileCardOpen ? 'Hide' : 'Current'} Location
          </Button>
        </div>
        
        {/* Desktop: Card that appears when button is clicked */}
        {isMobileCardOpen && (
          <div className="hidden md:block absolute top-16 left-4 z-20 w-full max-w-xs">
            <Card className="border-1 border-muted-foreground/20 shadow-lg bg-background/95 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    Current Location
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={currentLocation.status === "Moving" ? "default" : "secondary"}
                      className="flex items-center gap-1 text-xs h-5"
                    >
                      <Activity className={`h-3 w-3 ${currentLocation.status === "Moving" ? "animate-pulse" : ""}`} />
                      {currentLocation.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMobileCardOpen(false)}
                      className="h-5 w-5 p-0"
                    >
                      ×
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* IMEI Number (if available) */}
                {selectedDeviceId && (() => {
                  const vehicleInfo = deviceVehicleMap.get(selectedDeviceId);
                  const imeiNumber = vehicleInfo?.imei_number;
                  return imeiNumber ? (
                    <div className="space-y-1 pb-2 border-b border-muted-foreground/10">
                      <div className="text-[10px] text-muted-foreground">IMEI Number</div>
                      <div className="text-xs font-mono font-medium">{imeiNumber}</div>
                    </div>
                  ) : null;
                })()}
                {/* Address */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Navigation className="h-3.5 w-3.5" />
                    <span className="font-medium">Address</span>
                  </div>
                  <p className="text-xs font-medium pl-5 line-clamp-2">{currentLocation.address}</p>
                </div>

                {/* Coordinates */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-0.5">
                    <div className="text-[10px] text-muted-foreground">Latitude</div>
                    <div className="text-xs font-mono font-medium">{currentLocation.lat.toFixed(6)}</div>
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-[10px] text-muted-foreground">Longitude</div>
                    <div className="text-xs font-mono font-medium">{currentLocation.lng.toFixed(6)}</div>
                  </div>
                </div>

                {/* Speed */}
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg border-1 border-muted-foreground/20">
                  <div className="flex items-center gap-1.5">
                    <Gauge className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium">Speed</span>
                  </div>
                  <div className="text-xl font-bold text-primary">
                    {currentLocation.speed}
                    <span className="text-xs font-normal text-muted-foreground ml-1">km/h</span>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t border-muted-foreground/10">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3" />
                    <span>Last Updated</span>
                  </div>
                  <span className="font-mono">{currentLocation.timestamp}</span>
                </div>

                {/* Control Buttons */}
                <div className="flex items-center gap-1.5 pt-1.5 border-t border-muted-foreground/10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={togglePlayPause}
                    className={`flex-1 h-7 text-[11px] px-2 backdrop-blur-sm border-1 border-muted-foreground/20 ${
                      isDarkMode 
                        ? "bg-white text-black " 
                        : "bg-background/95"
                    }`}
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <>
                        <Pause className="h-3 w-3 mr-1" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="h-3 w-3 mr-1" />
                        Play
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={stopTracking}
                    className={`flex-1 h-7 text-[11px] px-2 backdrop-blur-sm border-1 border-muted-foreground/20 ${
                      isDarkMode 
                        ? "bg-white text-black " 
                        : "bg-background/95"
                    }`}
                    title="Stop"
                  >
                    <Square className="h-3 w-3 mr-1" />
                    Stop
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </>

      {/* Mobile Vehicle Card - Beautiful UI when vehicle is selected */}
      {isMobile && selectedDeviceId && isMobileVehicleCardOpen && (
        <div 
          className={`mobile-vehicle-card-container bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 border-t border-gray-200 dark:border-gray-700 text-foreground shadow-2xl ${cardState} ${isDragging ? 'dragging' : ''}`}
          style={{
            height: `${cardHeight}vh`,
            minHeight: `${cardHeight}vh`,
            maxHeight: `${cardHeight}vh`,
          }}
        >
          {/* Drag Handle */}
          <div 
            className="mobile-card-drag-handle"
            onTouchStart={(e) => {
              e.stopPropagation();
              handleDragStart(e);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleDragStart(e);
            }}
          ></div>

          <div className="p-4">
            {/* Header with Vehicle Info */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg glow-card">
                    <Navigation className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    {(() => {
                      const vehicleInfo = deviceVehicleMap.get(selectedDeviceId);
                      return vehicleInfo?.vehicle_number || vehicleInfo?.vehicle_name || 'Live Vehicle';
                    })()}
                  </h3>
                  <p className="text-[10px] text-gray-600 dark:text-gray-400">
                    {(() => {
                      const vehicleInfo = deviceVehicleMap.get(selectedDeviceId);
                      return vehicleInfo?.vehicle_name || 'Tracking Active';
                    })()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={expandMobileCard}
                  className="h-8 w-8 p-0 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  title={cardState === 'expanded' ? 'Normal Size' : 'Expand'}
                >
                  {cardState === 'expanded' ? '↕' : '⤢'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={collapseMobileCard}
                  className="h-8 w-8 p-0 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  title={cardState === 'collapsed' ? 'Normal Size' : 'Collapse'}
                >
                  {cardState === 'collapsed' ? '↕' : '⤡'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeMobileCard}
                  className="h-8 w-8 p-0 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  title="Close"
                >
                  ×
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {/* Status Badge */}
              <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-1.5">
                  <Activity className={`h-4 w-4 ${currentLocation.status === "Moving" ? "text-green-500 animate-pulse" : "text-gray-500"}`} />
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">Status</span>
                </div>
                <Badge 
                  variant={currentLocation.status === "Moving" ? "default" : "secondary"}
                  className="flex items-center gap-1 text-[10px] h-5 px-2"
                >
                  <div className={`w-1.5 h-1.5 rounded-full ${currentLocation.status === "Moving" ? "bg-green-500 animate-pulse" : "bg-gray-500"}`}></div>
                  {currentLocation.status}
                </Badge>
              </div>

              {/* Speed Card - Beautiful Design */}
              <div className="relative p-3 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-xl shadow-xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Gauge className="h-4 w-4 text-white" />
                      <span className="text-xs font-medium text-white/90">Speed</span>
                    </div>
                    <div className="px-2 py-0.5 bg-white/20 rounded-full">
                      <span className="text-[10px] font-semibold text-white">km/h</span>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">
                    {currentLocation.speed}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-white/80">
                    <Clock className="h-2.5 w-2.5" />
                    <span>Updated: {currentLocation.timestamp}</span>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <MapPin className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">Location</span>
                </div>
                <p className="text-[10px] text-gray-700 dark:text-gray-300 mb-2 line-clamp-2">{currentLocation.address}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <div className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5">Latitude</div>
                    <div className="text-[10px] font-mono font-semibold text-gray-900 dark:text-white">{currentLocation.lat.toFixed(6)}</div>
                  </div>
                  <div className="p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <div className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5">Longitude</div>
                    <div className="text-[10px] font-mono font-semibold text-gray-900 dark:text-white">{currentLocation.lng.toFixed(6)}</div>
                  </div>
                </div>
              </div>

              {/* Vehicle Details */}
              {(() => {
                const vehicleInfo = deviceVehicleMap.get(selectedDeviceId);
                if (vehicleInfo) {
                  return (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-2.5 rounded-lg border border-purple-200 dark:border-purple-700">
                      <h4 className="text-xs font-semibold text-purple-800 dark:text-purple-300 mb-1.5">Vehicle Details</h4>
                      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                        {vehicleInfo.vehicle_number && (
                          <div>
                            <span className="text-purple-600 dark:text-purple-400 font-medium">Vehicle No:</span>
                            <p className="text-gray-900 dark:text-white font-semibold text-xs">{vehicleInfo.vehicle_number}</p>
                          </div>
                        )}
                        {vehicleInfo.vehicle_name && (
                          <div>
                            <span className="text-purple-600 dark:text-purple-400 font-medium">Model:</span>
                            <p className="text-gray-900 dark:text-white font-semibold text-xs">{vehicleInfo.vehicle_name}</p>
                          </div>
                        )}
                        {vehicleInfo.imei_number && (
                          <div className="col-span-2">
                            <span className="text-purple-600 dark:text-purple-400 font-medium">IMEI:</span>
                            <p className="text-gray-900 dark:text-white font-mono text-[9px]">{vehicleInfo.imei_number}</p>
                          </div>
                        )}
      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Control Buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={togglePlayPause}
                  className={`flex-1 h-10 text-sm backdrop-blur-sm border-2 touch-manipulation ${
                    isPlaying
                      ? "bg-green-500 text-white border-green-600 hover:bg-green-600 active:bg-green-700"
                      : "bg-blue-500 text-white border-blue-600 hover:bg-blue-600 active:bg-blue-700"
                  }`}
                  title={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Play
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={stopTracking}
                  className="flex-1 h-10 text-sm bg-red-500 text-white border-red-600 hover:bg-red-600 active:bg-red-700 touch-manipulation"
                  title="Stop"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stop
                </Button>
              </div>

              {/* Route Info */}
              {route.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-blue-600 dark:text-blue-400 font-medium">Route Points</span>
                    <span className="text-gray-900 dark:text-white font-bold">{route.length}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Detailed Vehicle Card - Shows when marker is clicked */}
      {selectedMarkerData && !isMobile && (
        <div className="hidden md:block absolute bottom-24 left-4 z-30 w-full max-w-xs desktop-marker-detail-card">
          <Card className="border-1 border-muted-foreground/20 shadow-lg bg-background/95 backdrop-blur-sm">
            <CardHeader className="pb-1.5 pt-2 px-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <Navigation className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xs font-semibold text-foreground truncate">
                      {selectedMarkerData.vehicle_number || 'Vehicle'}
                    </CardTitle>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {selectedMarkerData.vehicle_name || 'No name'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedMarkerData(null)}
                  className="h-5 w-5 p-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                  title="Close"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-2.5 space-y-1.5">
              {/* Vehicle Information - Compact */}
              <div className="bg-blue-50 border border-blue-200 p-2 rounded">
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div>
                    <span className="text-blue-600 font-medium">Vehicle No:</span>
                    <p className="text-gray-900 font-semibold text-[11px]">{selectedMarkerData.vehicle_number || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Vehicle Name:</span>
                    <p className="text-gray-900 font-semibold text-[11px]">{selectedMarkerData.vehicle_name || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* GPS & Device Information - Compact */}
              <div className="bg-purple-50 border border-purple-200 p-2 rounded">
                <div className="space-y-1 text-[10px]">
                  <div>
                    <span className="text-purple-600 font-medium">IMEI:</span>
                    <p className="text-gray-900 font-mono text-[10px]">{selectedMarkerData.imei_number || 'N/A'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <span className="text-purple-600 font-medium">SIM No:</span>
                      <p className="text-gray-900 text-[10px]">{selectedMarkerData.sim_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-purple-600 font-medium">GPS Serial:</span>
                      <p className="text-gray-900 text-[10px] font-mono">{selectedMarkerData.gps_serial_no || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information - Compact */}
              <div className="bg-gray-50 p-2 rounded">
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div>
                    <span className="text-gray-600 font-medium">Lat:</span>
                    <p className="text-gray-900 font-mono text-[10px]">{selectedMarkerData.latitude?.toFixed(6) || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 font-medium">Lng:</span>
                    <p className="text-gray-900 font-mono text-[10px]">{selectedMarkerData.longitude?.toFixed(6) || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Date & Time Information - Compact */}
              <div className="bg-blue-50 border border-blue-200 p-2 rounded">
                <div className="space-y-1 text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-blue-600 font-medium">Last Update:</span>
                    <p className="text-gray-900 text-[10px]">
                      {selectedMarkerData.point_time 
                        ? new Date(selectedMarkerData.point_time).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                  {selectedMarkerData.activate_date && (
                    <div className="flex justify-between">
                      <span className="text-blue-600 font-medium">Activated:</span>
                      <p className="text-gray-900 text-[10px]">
                        {new Date(selectedMarkerData.activate_date).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

